import {
  Badge,
  Flex,
  FormControl,
  FormErrorMessage,
  Icon,
  IconButton,
  Input,
  Td,
  Text,
  Tr,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon, ChevronRightIcon, DeleteIcon } from "@chakra-ui/icons";
// The same icon the sidebar gives the requirement library, so the action points somewhere recognisable.
import { FiBookOpen } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import type {
  AwardCriterionDto,
  SourcingEventDto,
  SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { WeightType } from "../../../../../autogen/bff-api";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { rowOpenProps } from "../../../../../common/clickableRow";
import { priceModelType } from "../../../common/priceModel";
import { criterionStatus, subWeightTotal } from "../utils/awardCriteriaStatus";
import { criterionTypeLabelKey } from "../utils/publicProcurementOptions";
import { AwardStatusChip } from "./AwardStatusChip";
import { WeightInput } from "./WeightInput";

// t('Complete') t('No sub-criteria') t('Missing description') t('Sub-criterion weights {{total}} %')
// t('Delete criterion') t('Weight') t('Model: {{model}}') t('Criterion name')
// t('{{count}} sub-criterion') t('{{count}} sub-criterion_other')
// t('Expand {{criterion}}') t('Collapse {{criterion}}')
// t('Save to the requirement library') t('Already in the requirement library')

interface Props {
  eventId: string;
  index: number;
  criterion: AwardCriterionDto;
  isLocked: boolean;
  isPriceCriterion: boolean;
  priceModel: SourcingEventDto["eventData"]["priceModel"];
  onChange: (next: AwardCriterionDto) => void;
  onOpen: () => void;
  onDelete: () => void;
  /** Whether this criterion's requirements and its add-requirement row are showing below it. */
  isExpanded: boolean;
  onToggleExpanded: () => void;
  /** Just added from the library — marked until the confirmation above the table is dismissed. */
  isMarked?: boolean;
  /** Keeps this criterion for reuse. Omitted when the library is off or the user may not write to it. */
  onSaveToLibrary?: () => void;
  /** The library already holds a criterion by this name, so there is nothing left to keep. */
  isInLibrary?: boolean;
}

export const AwardCriterionRow = ({
  eventId,
  index,
  criterion,
  isLocked,
  isPriceCriterion,
  priceModel,
  onChange,
  onOpen,
  onDelete,
  isExpanded,
  onToggleExpanded,
  isMarked = false,
  onSaveToLibrary,
  isInLibrary = false,
}: Props) => {
  const { t } = useTranslation();
  const formatFieldError = useFieldErrorTranslation();
  const { fieldError } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const weightFieldError = fieldError(
    `eventData.awardCriteria.bestPriceQualityRatio.criteria.${index}.weight.number`
  );

  const status = criterionStatus(criterion);
  const subCriteria = criterion.subCriteria ?? [];
  const criterionName = criterion.name?.trim() || t(criterionTypeLabelKey(criterion.type));

  // Only the collapsed state distinguishes the two: an arrow when there is something folded away, a
  // plus when the criterion has no requirements yet, since opening it is how the first one gets added.
  // Open, every row behaves alike — same content showing, same click to close it — so they read alike.
  const toggleGlyph = isExpanded ? "chevron-down" : subCriteria.length ? "chevron-right" : "add";
  const toggleIcon = {
    "chevron-down": <ChevronDownIcon />,
    "chevron-right": <ChevronRightIcon />,
    add: <AddIcon boxSize="2.5" />,
  }[toggleGlyph];

  const statusChip = (() => {
    switch (status.kind) {
      case "complete":
        return <AwardStatusChip testId="award-criterion-status" tone="success" label={t("Complete")} />;
      case "no-sub-criteria":
        return <AwardStatusChip testId="award-criterion-status" tone="neutral" label={t("No sub-criteria")} />;
      case "missing-description":
        return (
          <AwardStatusChip testId="award-criterion-status" tone="warning" label={t("Missing description")} />
        );
      case "sub-weights-invalid":
        return (
          <AwardStatusChip
            testId="award-criterion-status"
            tone="warning"
            label={t("Sub-criterion weights {{total}} %", { total: status.total })}
          />
        );
    }
  })();

  const requirementSummary = (
    <Text
      fontSize="sm"
      color={status.kind === "sub-weights-invalid" ? "smWarning" : "smMuted"}
      data-testid="award-sub-weight-total"
      data-valid={status.kind !== "sub-weights-invalid"}
    >
      {subCriteria.length === 0
        ? "—"
        : status.kind === "sub-weights-invalid"
          ? t("{{count}} sub-criteria · weights {{total}} %", {
              count: subCriteria.length,
              total: subWeightTotal(criterion),
            })
          : t("{{count}} sub-criterion", { count: subCriteria.length })}
    </Text>
  );

  // A price row is scored from the tendered price rather than by an evaluator, so it names the model —
  // and keeps naming it once it has requirements, since those specify how the price weight is composed
  // without changing where the points come from.
  const summaryCell = isPriceCriterion ? (
    <Flex align="center" gap="2" wrap="wrap">
      <Badge bg="smPrimarySofter" color="smPrimary" px="2" py="1" borderRadius="md" textTransform="none">
        {t("Model: {{model}}", { model: t(priceModelType(priceModel)) })}
      </Badge>
      {subCriteria.length > 0 && requirementSummary}
    </Flex>
  ) : (
    requirementSummary
  );

  return (
    // The row opens on click, but only where nothing else already owns it: the name is edited inline,
    // the weight is a spinner, and the chevron and the bin each mean their own thing.
    <Tr
      data-testid="award-criterion-row"
      bg={isMarked ? "smSuccessSoft" : undefined}
      // The same hover the app's other clickable rows use, so "this opens something" is one habit.
      _hover={{ bg: "smBackgroundSecondary" }}
      {...rowOpenProps(onOpen)}
    >
      <Td>
        <Flex align="center" gap="3">
          <IconButton
            aria-label={
              (isExpanded
                ? t("Collapse {{criterion}}", { criterion: criterionName })
                : t("Expand {{criterion}}", { criterion: criterionName })) ?? ""
            }
            aria-expanded={isExpanded}
            data-toggle-glyph={toggleGlyph}
            icon={toggleIcon}
            variant="ghost"
            size="xs"
            flexShrink={0}
            onClick={onToggleExpanded}
          />
          <Badge
            bg="smBackgroundSecondary"
            color="smMuted"
            px="2"
            py="1"
            borderRadius="md"
            textTransform="none"
            fontSize="2xs"
            flexShrink={0}
          >
            {t(criterionTypeLabelKey(criterion.type))}
          </Badge>
          {/* No local buffer, unlike WeightInput: that one exists to keep half-typed numbers away from
              the coordinator, and a string needs none. updateField writes to the optimistic state on the
              keystroke even while the save is debounced, so the value survives the in-flight window. */}
          {/* Bold, and borderless until pointed at: the criterion is the level the 100 % weights apply
              to, so it has to out-read the requirements folded under it without the row turning into a
              form. Hover and focus still show the field, or nothing would say it is editable. */}
          <Input
            size="sm"
            rounded="md"
            flex="1"
            minW="14rem"
            fontWeight="semibold"
            borderColor="transparent"
            bg="transparent"
            _hover={{ borderColor: "smBorder" }}
            autoComplete="off"
            data-1p-ignore
            value={criterion.name ?? ""}
            placeholder={t("Criterion name") ?? ""}
            aria-label={t("Criterion name") ?? ""}
            isDisabled={isLocked}
            onChange={(e) => onChange({ ...criterion, name: e.target.value })}
          />
        </Flex>
      </Td>
      <Td>
        <FormControl isInvalid={!!weightFieldError}>
          <WeightInput
            value={criterion.weight?.number}
            isDisabled={isLocked}
            isInvalid={!!weightFieldError}
            isQuiet
            ariaLabel={t("Weight")}
            onChange={(weight) =>
              onChange({
                ...criterion,
                weight: weight == null ? undefined : { number: weight, type: WeightType.Percentage },
              })
            }
          />
          {weightFieldError && (
            <FormErrorMessage fontSize="xs">{formatFieldError(weightFieldError)}</FormErrorMessage>
          )}
        </FormControl>
      </Td>
      <Td>{summaryCell}</Td>
      <Td>{statusChip}</Td>
      <Td textAlign="right" whiteSpace="nowrap">
        {onSaveToLibrary && (
          <IconButton
            aria-label={
              (isInLibrary ? t("Already in the requirement library") : t("Save to the requirement library")) ?? ""
            }
            icon={<Icon as={FiBookOpen} />}
            variant="ghost"
            size="xs"
            isDisabled={isLocked || isInLibrary}
            onClick={onSaveToLibrary}
          />
        )}
        <IconButton
          aria-label={t("Delete criterion") ?? "Delete"}
          icon={<DeleteIcon />}
          variant="ghost"
          size="xs"
          isDisabled={isLocked}
          onClick={onDelete}
        />
      </Td>
    </Tr>
  );
};
